/**
 * Geo name normalization for map chart blocks (chartjs-chart-geo).
 *
 * CRM geo fields hold free-text values ("CA", "California", "USA",
 * "United States") while the TopoJSON atlases the map charts draw from key
 * features by one canonical `properties.name` ("California",
 * "United States of America"). This module owns the alias → canonical-name
 * mapping so every generated map block joins data to features the same way,
 * instead of each block regenerating its own (typo-prone) lookup table.
 *
 * Canonical names match the pinned atlases used by map blocks:
 *   - us-atlas@3.0.1 states-10m.json   (`properties.name` = full state name)
 *   - world-atlas@2.0.2 countries-50m.json (Natural Earth short names, e.g.
 *     "United States of America", "Czechia", "South Korea")
 *
 * Kept dependency-free so it stays testable under the webapp's jest config.
 */

const US_STATE_NAME_BY_CODE: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  PR: "Puerto Rico",
};

// Common CRM spellings → world-atlas (Natural Earth) short names. Only entries
// that actually differ from the canonical name belong here; same-cased matches
// already resolve via the case-insensitive comparison in normalizeGeoName.
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  "UNITED STATES": "United States of America",
  USA: "United States of America",
  US: "United States of America",
  "U.S.": "United States of America",
  "U.S.A.": "United States of America",
  AMERICA: "United States of America",
  UK: "United Kingdom",
  "U.K.": "United Kingdom",
  "GREAT BRITAIN": "United Kingdom",
  ENGLAND: "United Kingdom",
  "CZECH REPUBLIC": "Czechia",
  "SOUTH KOREA": "South Korea",
  KOREA: "South Korea",
  "REPUBLIC OF KOREA": "South Korea",
  "KOREA, REPUBLIC OF": "South Korea",
  "RUSSIAN FEDERATION": "Russia",
  UAE: "United Arab Emirates",
  "VIET NAM": "Vietnam",
  BURMA: "Myanmar",
  "IVORY COAST": "Côte d'Ivoire",
  "DEMOCRATIC REPUBLIC OF THE CONGO": "Dem. Rep. Congo",
  "DR CONGO": "Dem. Rep. Congo",
  "THE NETHERLANDS": "Netherlands",
  HOLLAND: "Netherlands",
  "BOSNIA AND HERZEGOVINA": "Bosnia and Herz.",
  "DOMINICAN REPUBLIC": "Dominican Rep.",
  "CENTRAL AFRICAN REPUBLIC": "Central African Rep.",
  "SOUTH SUDAN": "S. Sudan",
  "EQUATORIAL GUINEA": "Eq. Guinea",
  ESWATINI: "eSwatini",
  SWAZILAND: "eSwatini",
  MACEDONIA: "Macedonia",
  "NORTH MACEDONIA": "Macedonia",
};

/**
 * Normalize a raw geo value from data (US state code, country alias, or an
 * already-canonical name in any casing) to the canonical TopoJSON feature
 * name. Unrecognized values are returned trimmed, so callers can still do a
 * case-insensitive join and surface the misses.
 */
export function normalizeGeoName(raw: unknown): string {
  if (raw === null || raw === undefined) {
    return "";
  }
  const trimmed = String(raw).trim();
  const upper = trimmed.toUpperCase();
  return US_STATE_NAME_BY_CODE[upper] ?? COUNTRY_NAME_ALIASES[upper] ?? trimmed;
}

/**
 * Build a per-feature-name value map from data rows: normalizes each row's geo
 * key, sums values into the canonical name (lower-cased for case-insensitive
 * joining against `feature.properties.name`), and reports keys that resolved
 * to nothing so blocks can `console.warn` them instead of dropping data
 * silently.
 */
export function buildGeoValueMap(
  rows: Array<Record<string, unknown>>,
  geoField: string,
  valueField: string,
  featureNames: string[]
): { valueByFeatureKey: Map<string, number>; unmatchedKeys: string[] } {
  const featureKeySet = new Set(featureNames.map(n => n.toLowerCase()));
  const valueByFeatureKey = new Map<string, number>();
  const unmatched = new Set<string>();

  for (const row of rows) {
    const rawKey = row[geoField];
    const canonical = normalizeGeoName(rawKey);
    if (!canonical) {
      continue;
    }
    const featureKey = canonical.toLowerCase();
    if (!featureKeySet.has(featureKey)) {
      unmatched.add(String(rawKey));
      continue;
    }
    const value = Number(row[valueField]) || 0;
    valueByFeatureKey.set(featureKey, (valueByFeatureKey.get(featureKey) ?? 0) + value);
  }

  return { valueByFeatureKey, unmatchedKeys: [...unmatched] };
}
